import { useMemo } from 'react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import '../styles/AdminUsageChart.css';

const COLOR_CASES = '#0D9488';
const COLOR_COST = '#D97706';

function formatDateLabel(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function prepareChartData(daily, userSeries = []) {
    if (!daily || daily.length === 0) return [];

    // Backend returns newest-first; reverse to chronological
    const sorted = [...daily].sort((a, b) => a.date.localeCompare(b.date));

    // Fill date gaps with zero-value entries
    const filled = [];
    const start = new Date(sorted[0].date + 'T00:00:00');
    const end = new Date(sorted[sorted.length - 1].date + 'T00:00:00');
    const lookup = Object.fromEntries(sorted.map((d) => [d.date, d]));

    for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
        const key = dt.toISOString().slice(0, 10);
        const entry = lookup[key];
        filled.push({
            date: key,
            label: formatDateLabel(key),
            cases: entry?.total_cases_processed ?? 0,
            cost: entry?.total_cost_usd ?? 0,
            prompt_tokens: entry?.total_prompt_tokens ?? 0,
            completion_tokens: entry?.total_completion_tokens ?? 0,
            cached_tokens: entry?.total_cached_tokens ?? 0,
            ...Object.fromEntries(
                userSeries.map((u) => [
                    `user_${u.uid}`,
                    entry?.by_user?.[u.uid]?.processed ?? 0,
                ])
            ),
        });
    }

    return filled;
}

function CustomTooltip({ active, payload, showCost, userSeries }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const hasUsers = userSeries?.length > 0;
    return (
        <div className="admin-chart-tooltip">
            <p className="admin-chart-tooltip-date">{d.date}</p>
            <div className="admin-chart-tooltip-row">
                <span
                    className="admin-chart-tooltip-swatch"
                    style={{ backgroundColor: COLOR_CASES }}
                />
                <span>{hasUsers ? 'Total:' : 'Cases:'}</span>
                <strong>{d.cases}</strong>
            </div>
            {hasUsers && userSeries.map((u) => (
                <div key={u.uid} className="admin-chart-tooltip-row secondary">
                    <span
                        className="admin-chart-tooltip-swatch"
                        style={{ backgroundColor: u.color }}
                    />
                    <span>{u.name}:</span>
                    <strong>{d[`user_${u.uid}`] ?? 0}</strong>
                </div>
            ))}
            {showCost && d.cost != null && (
                <div className="admin-chart-tooltip-row">
                    <span
                        className="admin-chart-tooltip-swatch"
                        style={{ backgroundColor: COLOR_COST }}
                    />
                    <span>Cost:</span>
                    <strong>${d.cost.toFixed(4)}</strong>
                </div>
            )}
            {showCost && d.prompt_tokens != null && (
                <>
                    <div className="admin-chart-tooltip-divider" />
                    <div className="admin-chart-tooltip-row secondary">
                        <span>Prompt:</span>
                        <span>{d.prompt_tokens.toLocaleString()}</span>
                    </div>
                    <div className="admin-chart-tooltip-row secondary">
                        <span>Completion:</span>
                        <span>{d.completion_tokens.toLocaleString()}</span>
                    </div>
                    <div className="admin-chart-tooltip-row secondary">
                        <span>Cached:</span>
                        <span>{d.cached_tokens.toLocaleString()}</span>
                    </div>
                </>
            )}
        </div>
    );
}

function CustomLegend({ showCost, userSeries }) {
    const hasUsers = userSeries?.length > 0;
    return (
        <div className="admin-chart-legend">
            <span className="admin-chart-legend-item">
                <span
                    className="admin-chart-legend-swatch"
                    style={{ backgroundColor: COLOR_CASES }}
                />
                {hasUsers ? 'Total' : 'Cases'}
            </span>
            {hasUsers && userSeries.map((u) => (
                <span key={u.uid} className="admin-chart-legend-item">
                    <span
                        className="admin-chart-legend-swatch"
                        style={{ backgroundColor: u.color }}
                    />
                    {u.name}
                </span>
            ))}
            {showCost && (
                <span className="admin-chart-legend-item">
                    <span
                        className="admin-chart-legend-swatch admin-chart-legend-swatch--dashed"
                        style={{ borderColor: COLOR_COST }}
                    />
                    Cost
                </span>
            )}
        </div>
    );
}

function AdminUsageChart({ daily, showCost = true, userSeries = [] }) {
    const data = useMemo(() => prepareChartData(daily, userSeries), [daily, userSeries]);

    if (data.length < 2) return null;

    return (
        <div className="admin-chart-card">
            <div className="admin-chart-header">
                <h3>Usage Over Time</h3>
                <CustomLegend showCost={showCost} userSeries={userSeries} />
            </div>
            <ResponsiveContainer width="100%" height={320}>
                <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#E8E8E2"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fill: '#9494A3' }}
                        tickLine={false}
                        axisLine={{ stroke: '#E8E8E2' }}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        yAxisId="cases"
                        tick={{ fontSize: 12, fill: '#9494A3' }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                        width={36}
                    />
                    {showCost && (
                        <YAxis
                            yAxisId="cost"
                            orientation="right"
                            tick={{ fontSize: 12, fill: '#9494A3' }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => `$${v.toFixed(2)}`}
                            width={56}
                        />
                    )}
                    <Tooltip content={<CustomTooltip showCost={showCost} userSeries={userSeries} />} />
                    <Line
                        yAxisId="cases"
                        type="monotone"
                        dataKey="cases"
                        stroke={COLOR_CASES}
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 4, fill: COLOR_CASES }}
                    />
                    {userSeries.map((u) => (
                        <Line
                            key={u.uid}
                            yAxisId="cases"
                            type="monotone"
                            dataKey={`user_${u.uid}`}
                            stroke={u.color}
                            strokeWidth={1.25}
                            dot={false}
                            activeDot={{ r: 3, fill: u.color }}
                        />
                    ))}
                    {showCost && (
                        <Line
                            yAxisId="cost"
                            type="monotone"
                            dataKey="cost"
                            stroke={COLOR_COST}
                            strokeWidth={2}
                            strokeDasharray="6 4"
                            dot={false}
                            activeDot={{ r: 4, fill: COLOR_COST }}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export default AdminUsageChart;
